import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import LegacyFilterToolbar, {
  LEGACY_FILTER_INPUT_CLASS,
} from "components/Common/LegacyFilterToolbar";
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    FormFeedback,
    Input,
    Row,
} from "reactstrap";
import * as Yup from "yup";
import { normalizeMetadataFilterDateForApi } from "helpers/metadataFilterHelpers";
import { toast } from "react-toastify";

import CommonTable from "components/Common/TableContainer";
import Loaders from "components/Common/Loaders";
import { AsyncClientSelect } from "components/Common/AsyncSearchSelects";
import {
    applyClientAsyncFilterOptionToFormState,
    buildClientAsyncFilterInitialOption,
    getClientAsyncFilterLabelFieldKey,
} from "helpers/metadataFilterHelpers";
import { submitEligibleReportFilterData } from "model/EligibleReportModel";

const TABLE_COLUMNS = [
    { Header: "Client", accessor: "client" },
    { Header: "Contact", accessor: "contact" },
    { Header: "Total Eligible", accessor: "totalEligible" },
    { Header: "Total RTR", accessor: "totalRtr" },
    {
        Header: "Days Since Last Update Was Sent",
        accessor: "daysSinceLastUpdateWasSent",
    },
];

const strOrNull = (value) => {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    return normalized === "" ? null : normalized;
};

const eligibleReportFilterSchema = Yup.object().shape({
    fromDate: Yup.string().trim().required("Start date is required"),
    toDate: Yup.string().trim().required("End date is required"),
});

const FilterFieldFeedback = ({ message = "" }) => (
    <div className="filter-field-feedback-slot" aria-live="polite">
        {message ? (
            <FormFeedback type="invalid" className="d-block filter-field-feedback mb-0">
                {message}
            </FormFeedback>
        ) : null}
    </div>
);

FilterFieldFeedback.propTypes = {
    message: PropTypes.string,
};

const extractFilterResponse = (response = {}) => {
    const responseData = response?.data ?? response ?? {};
    const nestedData =
        responseData?.data && typeof responseData.data === "object"
            ? responseData.data
            : responseData;

    const rows = Array.isArray(nestedData?.rows)
        ? nestedData.rows
        : Array.isArray(responseData?.rows)
            ? responseData.rows
            : Array.isArray(nestedData?.records)
                ? nestedData.records
                : Array.isArray(responseData?.records)
                    ? responseData.records
                    : Array.isArray(nestedData?.data)
                        ? nestedData.data
                        : Array.isArray(responseData?.data)
                            ? responseData.data
                            : Array.isArray(responseData)
                                ? responseData
                                : [];

    return { rows };
};

const mapRowsFromApi = (rows = []) =>
    (Array.isArray(rows) ? rows : []).map((item = {}, index) => ({
        ...item,
        id: item?.clientId ?? item?.id ?? item?._id ?? `row-${index}`,
        serialNumber: item?.serialNumber ?? index + 1,
        client: item?.companyName ?? item?.client ?? item?.clientName ?? "",
        contact: item?.recConName ?? item?.contact ?? item?.contactName ?? "",
        totalEligible: item?.totalEligible ?? 0,
        totalRtr: item?.totalRtr ?? 0,
        daysSinceLastUpdateWasSent:
            item?.lastMailRelative ??
            item?.daysSinceLastUpdateWasSent ??
            "",
        lastMailDate: item?.lastMailDate ?? "",
    }));

const EligibleReport = () => {
    const [filterForm, setFilterForm] = useState({
        fromDate: "",
        toDate: "",
        clientId: "",
        [getClientAsyncFilterLabelFieldKey()]: "",
    });
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterErrors, setFilterErrors] = useState({});

    const filteredData = useMemo(() => reportData, [reportData]);

    const buildFilterPayload = () => ({
        clientId: strOrNull(filterForm?.clientId),
        dateFrom: normalizeMetadataFilterDateForApi(filterForm?.fromDate),
        dateTo: normalizeMetadataFilterDateForApi(filterForm?.toDate),
    });

    const getEligibleReportList = async () => {
        try {
            await eligibleReportFilterSchema.validate(filterForm, { abortEarly: false });
            setFilterErrors({});
        } catch (validationError) {
            const nextErrors = {};
            validationError?.inner?.forEach((error) => {
                nextErrors[error.path] = error.message;
            });
            setFilterErrors(nextErrors);
            return;
        }

        setLoading(true);
        try {
            const response = await submitEligibleReportFilterData(buildFilterPayload());
            const { rows } = extractFilterResponse(response);

            if (response == null || response?.success === false) {
                setReportData([]);
                if (response?.message) {
                    toast.error(response.message);
                }
                return;
            }

            setReportData(mapRowsFromApi(rows));
        } catch (error) {
            toast.error(error?.message || "Unable to load eligible report");
            setReportData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterFieldChange = (event) => {
        const { name, value } = event.target;
        setFilterForm((previous) => ({
            ...previous,
            [name]: value,
        }));
        if (filterErrors?.[name]) {
            setFilterErrors((previous) => ({
                ...previous,
                [name]: "",
            }));
        }
    };

    return (
        <React.Fragment>
            <div className="page-content eligible-report-page">
                <Container fluid>
                    <Card>
                        <CardBody>
                            <h5 className="fw-bold mb-3">Eligible Report</h5>
                            <hr />

                            <LegacyFilterToolbar className="w-100">
                                <Row className="metadata-filter-toolbar__row g-2 g-md-3 align-items-end">
                                    <Col xs="12" md="6" lg={3}>
                                        <span className="manage-user-filter-label">Start Date</span>
                                        <Input
                                            type="date"
                                            name="fromDate"
                                            className={`${LEGACY_FILTER_INPUT_CLASS}${
                                                filterErrors?.fromDate ? " is-invalid" : ""
                                            }`}
                                            value={filterForm?.fromDate || ""}
                                            onChange={handleFilterFieldChange}
                                        />
                                        <FilterFieldFeedback message={filterErrors?.fromDate} />
                                    </Col>
                                    <Col xs="12" md="6" lg={3}>
                                        <span className="manage-user-filter-label">End Date</span>
                                        <Input
                                            type="date"
                                            name="toDate"
                                            className={`${LEGACY_FILTER_INPUT_CLASS}${
                                                filterErrors?.toDate ? " is-invalid" : ""
                                            }`}
                                            value={filterForm?.toDate || ""}
                                            onChange={handleFilterFieldChange}
                                        />
                                        <FilterFieldFeedback message={filterErrors?.toDate} />
                                    </Col>
                                    <Col xs="12" md="6" lg={3}>
                                        <span className="manage-user-filter-label">Client</span>
                                        <AsyncClientSelect
                                            key={`eligible-report-client-${filterForm?.clientId || ""}`}
                                            pageName="ReportFiltered"
                                            value={filterForm?.clientId || ""}
                                            initialOption={buildClientAsyncFilterInitialOption(
                                                filterForm?.clientId,
                                                filterForm[getClientAsyncFilterLabelFieldKey()]
                                            )}
                                            onChange={(option) =>
                                                setFilterForm((previous) =>
                                                    applyClientAsyncFilterOptionToFormState(
                                                        previous,
                                                        option
                                                    )
                                                )
                                            }
                                            placeholder="Select Client"
                                        />
                                        <FilterFieldFeedback />
                                    </Col>
                                    <Col xs="12" md="6" lg={2}>
                                        <span
                                            className="manage-user-filter-label"
                                            aria-hidden="true"
                                        >
                                            &nbsp;
                                        </span>
                                        <Button
                                            color="primary"
                                            className="w-100 manage-user-search-btn"
                                            type="button"
                                            onClick={getEligibleReportList}
                                        >
                                            <i className="fa fa-search me-1" aria-hidden="true" />
                                            Search
                                        </Button>
                                        <FilterFieldFeedback />
                                    </Col>
                                </Row>
                            </LegacyFilterToolbar>

                            {loading ? (
                                <Loaders />
                            ) : (
                                <CommonTable
                                    columns={TABLE_COLUMNS}
                                    data={filteredData}
                                    isActionRequired={false}
                                    pageName="EligibleReport"
                                />
                            )}
                        </CardBody>
                    </Card>
                </Container>
            </div>
        </React.Fragment>
    );
};

EligibleReport.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(EligibleReport);
